import { useReducer, useEffect } from 'react';
import { isCancel } from 'axios';
import { message } from 'antd';
import { instance } from 'store/utils';
import { Errors } from 'utils/errors';
import { useDebounce } from 'hooks';

/**
 * @description
 * Handles the **retrieval of regions query** with search-as-you-type functionality.
 *
 * @param {Object} props - Hook props object.
 * @param {string} props.search - Search query for SAYT.
 * @param {Array} props.selected - Already selected regions.
 *
 * @returns {{
 *   data: object | null,
 *   isFetching: boolean,
 *   isError: boolean,
 *   error: unknown | null,
 *   isEmpty: boolean,
 * }}
 */
export function useRegionHQOptionsQuery({ search = '', selected = [] }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.trim().length === 0) {
      dispatch({ type: 'QUERY_RESET' });
      return;
    }

    const controller = new AbortController();

    async function fetchRegionHQOptions() {
      dispatch({ type: 'QUERY_START' });
      try {
        const res = await instance.get(`/proposal/funding-programs/regions`, {
          params: { level: 1, regionLabel: debouncedSearch },
          signal: controller.signal,
        });
        const filteredData = (res.data ?? []).filter(
          item => !selected.some(sel => sel.code === item.code)
        );
        dispatch({ type: 'QUERY_SUCCESS', payload: filteredData });
      } catch (err) {
        if (isCancel(err)) {
          dispatch({ type: 'QUERY_CANCELLED' });
          return;
        }
        const errorMessage = Errors.GenericRetrievalError;
        message.error(errorMessage);
        dispatch({ type: 'QUERY_ERROR', payload: err });
      }
    }

    fetchRegionHQOptions();

    return () => {
      controller.abort();
    };
  }, [debouncedSearch, selected]);

  return state;
}

const initialState = {
  data: null,
  isFetching: false,
  isError: false,
  error: null,
  isEmpty: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'QUERY_START': {
      return {
        ...state,
        isFetching: true,
        isError: false,
        error: null,
      };
    }
    case 'QUERY_SUCCESS': {
      return {
        data: action.payload,
        isFetching: false,
        isError: false,
        error: null,
        isEmpty: !action.payload?.length,
      };
    }
    case 'QUERY_ERROR': {
      return {
        data: null,
        isFetching: false,
        isError: true,
        error: action.payload,
        isEmpty: false,
      };
    }
    case 'QUERY_CANCELLED': {
      return { ...state, isFetching: false };
    }
    case 'QUERY_RESET': {
      return initialState;
    }
    default: {
      return state;
    }
  }
}
